import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import 'mathlive';

const MathLiveField = forwardRef(
  ({ value, onChange, disabled, onEnter, onFocus, borderColor }, externalRef) => {
    const ref = useRef(null);
    const cbRef = useRef({});
    cbRef.current = { onChange, onEnter, onFocus };

    // Expose insertLatex and focus to parent via ref
    useImperativeHandle(externalRef, () => ({
      insertLatex: (latex) => {
        const el = ref.current;
        if (!el) return;
        el.focus();
        el.insert(latex, { selectionMode: 'placeholder', format: 'latex' });
      },
      focus: () => ref.current?.focus(),
    }));

    // Attach events once
    useEffect(() => {
      const el = ref.current;
      if (!el) return;

      const hideChrome = () => {
        // Disable virtual keyboard
        el.mathVirtualKeyboardPolicy = 'manual';

        // Clear context menu
        el.menuItems = [];
        try {
          el.setOptions?.({ menu: { items: [] } });
        } catch (_) {}

        // Inject CSS directly into MathLive's shadow root to hide the
        // keyboard toggle and settings buttons regardless of their exact names
        const shadow = el.shadowRoot;
        if (shadow && 'adoptedStyleSheets' in shadow && !el.__mathliveChromeHiddenStylesApplied) {
          const sheet = new CSSStyleSheet();
          sheet.replaceSync(`
            [part="virtual-keyboard-toggle"],
            [part="menu-toggle"],
            .ML__virtual-keyboard-toggle,
            .ML__menu-toggle,
            .ML__toolbar-button { display: none !important; }
          `);
          shadow.adoptedStyleSheets = [...shadow.adoptedStyleSheets, sheet];
          el.__mathliveChromeHiddenStylesApplied = true;
        }
      };

      // MathLive may initialise asynchronously — run on ready or immediately
      if (el.shadowRoot) {
        hideChrome();
      } else {
        el.addEventListener('mount', hideChrome, { once: true });
      }

      const handleInput = () => cbRef.current.onChange?.(el.value);
      const handleKeydown = (e) => {
        if (e.key === 'Enter') cbRef.current.onEnter?.();
      };
      const handleFocus = () => cbRef.current.onFocus?.();

      el.addEventListener('input', handleInput);
      el.addEventListener('keydown', handleKeydown);
      el.addEventListener('focusin', handleFocus);

      return () => {
        el.removeEventListener('input', handleInput);
        el.removeEventListener('keydown', handleKeydown);
        el.removeEventListener('focusin', handleFocus);
        el.removeEventListener('mount', hideChrome);
      };
    }, []);

    // Sync value from parent → field
    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      if (el.value !== (value ?? '')) el.value = value ?? '';
    }, [value]);

    // Sync read-only
    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      disabled ? el.setAttribute('read-only', '') : el.removeAttribute('read-only');
    }, [disabled]);

    return (
      <math-field
        ref={ref}
        style={{
          display: 'block',
          width: '100%',
          fontSize: '1rem',
          fontFamily: 'inherit',
          padding: '0.4rem 0.75rem',
          border: `1.5px solid ${borderColor ?? '#d1d5db'}`,
          borderRadius: '0.5rem',
          outline: 'none',
          background: disabled ? '#f9fafb' : '#ffffff',
          minHeight: '2.5rem',
          boxSizing: 'border-box',
          transition: 'border-color 0.15s',
        }}
      />
    );
  }
);

export default MathLiveField;